import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import type { AggregateFn, FilterOperator, ReportConfig } from '@accounting/shared';
import {
  useDeleteSavedReport,
  useReportCatalog,
  useRunReport,
  useSaveReport,
  useSavedReports,
} from '../../hooks/useReportBuilder';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/report-builder/')({
  component: ReportBuilderPage,
});

interface FilterRow {
  field: string;
  operator: FilterOperator;
  raw: string;
}
interface MeasureRow {
  field: string; // '' = row count
  aggregate: AggregateFn;
}

function ReportBuilderPage() {
  const companyId = useActiveCompanyId();
  const { data: sources = [] } = useReportCatalog(companyId);
  const { data: saved = [] } = useSavedReports(companyId);
  const run = useRunReport(companyId);
  const saveReport = useSaveReport(companyId);
  const delReport = useDeleteSavedReport(companyId);

  const ds = sources[0]; // single data source for now (journal lines)
  const fields = useMemo(() => ds?.fields ?? [], [ds]);
  const groupFields = fields.filter((f) => f.capabilities.includes('group'));
  const measureFields = fields.filter((f) => f.capabilities.includes('measure'));
  const filterFields = fields.filter((f) => f.capabilities.includes('filter'));
  const fieldByKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);

  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [measures, setMeasures] = useState<MeasureRow[]>([]);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [reportName, setReportName] = useState('');

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Report Builder</h1>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  const buildConfig = (): ReportConfig => ({
    dataSource: ds?.key ?? 'journal_lines',
    filters: filters
      .filter((f) => f.field)
      .map((f) => ({
        field: f.field,
        operator: f.operator,
        value:
          f.operator === 'in' || f.operator === 'between'
            ? f.raw.split(',').map((s) => s.trim()).filter(Boolean)
            : f.raw,
      })),
    groupBy,
    measures: measures.map((m) => ({ field: m.field || '*', aggregate: m.field ? m.aggregate : 'count' })),
  });

  const loadConfig = (cfg: ReportConfig) => {
    setGroupBy(cfg.groupBy ?? []);
    setMeasures((cfg.measures ?? []).map((m) => ({ field: m.field === '*' ? '' : m.field, aggregate: m.aggregate })));
    setFilters(
      (cfg.filters ?? []).map((f) => ({
        field: f.field,
        operator: f.operator,
        raw: Array.isArray(f.value) ? f.value.join(', ') : f.value ?? '',
      })),
    );
  };

  const runError =
    run.error instanceof ApiError
      ? (run.error.body as { error?: string } | null)?.error ?? 'Report failed'
      : run.error?.message;
  const result = run.data;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Report Builder</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Build a custom report from your transactions</p>
        </div>
        {saved.length > 0 && (
          <label style={{ fontSize: 13 }}>
            Saved{' '}
            <select
              defaultValue=""
              onChange={(e) => {
                const r = saved.find((s) => s.id === e.target.value);
                if (r) loadConfig(r.config);
              }}
            >
              <option value="">— load —</option>
              {saved.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        {/* Filters */}
        <h2 style={{ fontSize: 15, marginTop: 0 }}>Filters</h2>
        {filters.map((f, i) => {
          const fd = fieldByKey.get(f.field);
          return (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={f.field} onChange={(e) => setFilters((p) => p.map((x, j) => (j === i ? { ...x, field: e.target.value, operator: (fieldByKey.get(e.target.value)?.validOperators[0] ?? 'eq') as FilterOperator } : x)))}>
                <option value="">— field —</option>
                {filterFields.map((ff) => <option key={ff.key} value={ff.key}>{ff.label}</option>)}
              </select>
              <select value={f.operator} onChange={(e) => setFilters((p) => p.map((x, j) => (j === i ? { ...x, operator: e.target.value as FilterOperator } : x)))} disabled={!fd}>
                {(fd?.validOperators ?? []).map((op) => <option key={op} value={op}>{op}</option>)}
              </select>
              <input
                value={f.raw}
                placeholder={f.operator === 'in' ? 'a, b, c' : f.operator === 'between' ? 'min, max' : fd?.enumValues?.length ? fd.enumValues.join(' | ') : 'value'}
                onChange={(e) => setFilters((p) => p.map((x, j) => (j === i ? { ...x, raw: e.target.value } : x)))}
                style={{ flex: 1, minWidth: 160 }}
              />
              <button className="btn-danger" onClick={() => setFilters((p) => p.filter((_, j) => j !== i))}>×</button>
            </div>
          );
        })}
        <button className="btn-secondary" onClick={() => setFilters((p) => [...p, { field: '', operator: 'eq', raw: '' }])}>+ Add filter</button>

        {/* Group by */}
        <h2 style={{ fontSize: 15, margin: '16px 0 6px' }}>Group by</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {groupFields.map((gf) => {
            const on = groupBy.includes(gf.key);
            return (
              <button
                key={gf.key}
                className={on ? 'btn-primary' : 'btn-secondary'}
                onClick={() => setGroupBy((p) => (on ? p.filter((k) => k !== gf.key) : [...p, gf.key]))}
              >
                {gf.label}
              </button>
            );
          })}
        </div>
        <p className="muted" style={{ fontSize: 12 }}>No group-by → a detail list of matching lines.</p>

        {/* Measures */}
        <h2 style={{ fontSize: 15, margin: '8px 0 6px' }}>Measures</h2>
        {measures.map((m, i) => {
          const fd = m.field ? fieldByKey.get(m.field) : null;
          return (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <select value={m.field} onChange={(e) => setMeasures((p) => p.map((x, j) => (j === i ? { ...x, field: e.target.value, aggregate: e.target.value ? (fieldByKey.get(e.target.value)?.validAggregates[0] ?? 'sum') as AggregateFn : 'count' } : x)))}>
                <option value="">Row count</option>
                {measureFields.map((mf) => <option key={mf.key} value={mf.key}>{mf.label}</option>)}
              </select>
              <select value={m.aggregate} onChange={(e) => setMeasures((p) => p.map((x, j) => (j === i ? { ...x, aggregate: e.target.value as AggregateFn } : x)))} disabled={!m.field}>
                {(m.field ? fd?.validAggregates ?? [] : ['count']).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <button className="btn-danger" onClick={() => setMeasures((p) => p.filter((_, j) => j !== i))}>×</button>
            </div>
          );
        })}
        <button className="btn-secondary" onClick={() => setMeasures((p) => [...p, { field: '', aggregate: 'count' }])}>+ Add measure</button>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={() => run.mutate(buildConfig())} disabled={run.isPending}>
            {run.isPending ? 'Running…' : 'Run report'}
          </button>
          <input value={reportName} onChange={(e) => setReportName(e.target.value)} placeholder="Save as…" style={{ maxWidth: 200 }} />
          <button
            className="btn-secondary"
            disabled={!reportName.trim() || saveReport.isPending}
            onClick={() => saveReport.mutate({ name: reportName.trim(), config: buildConfig() }, { onSuccess: () => setReportName('') })}
          >
            Save
          </button>
        </div>
        {runError && <div className="field-error" style={{ marginTop: 12 }}>{runError}</div>}
      </div>

      {result && (
        <div className="card" style={{ padding: 16, overflowX: 'auto' }}>
          <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
            {result.rowCount} row{result.rowCount === 1 ? '' : 's'} ({result.mode}){result.truncated ? ' — truncated' : ''}
          </div>
          <table className="table">
            <thead>
              <tr>
                {result.columns.map((c) => (
                  <th key={c.key} style={{ textAlign: c.isMeasure ? 'right' : 'left' }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ textAlign: result.columns[ci]?.isMeasure ? 'right' : 'left' }}>{cell ?? ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            {result.totals.some((t) => t !== null) && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  {result.totals.map((t, ci) => (
                    <td key={ci} style={{ textAlign: result.columns[ci]?.isMeasure ? 'right' : 'left' }}>{ci === 0 && t === null ? 'Total' : t ?? ''}</td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {saved.length > 0 && (
        <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          Saved reports:{' '}
          {saved.map((s) => (
            <span key={s.id} style={{ marginRight: 10 }}>
              {s.name}{' '}
              <button className="btn-secondary" style={{ padding: '0 6px' }} onClick={() => delReport.mutate(s.id)} title="Delete">×</button>
            </span>
          ))}
        </p>
      )}

      <p style={{ marginTop: 16 }}>
        <Link to="/reports" style={{ fontSize: 13 }}>← Reports</Link>
      </p>
    </div>
  );
}
